import { defineStore } from 'pinia';
import { reactive, toRefs } from 'vue';
import { cloneDeep } from 'lodash-es';
import {
	setLocalStorageItem,
	getLocalStorageItem,
	removeLocalStorageItem,
	EStorageKey,
} from '@/utils/localStorage';
import type { ApiV1 } from '@teamworksdev/influencer/core';
import { useAuthService } from '@/api/authService';
import { handleError } from '@/utils/errorHandling';


export const isAuthenticated = () => !!getLocalStorageItem<ApiV1.AuthToken>('inflcr-token');

export const isImpersonating = () => !!getLocalStorageItem<ApiV1.AuthToken>('inflcr-impersonation-token');

export const useAuthStore = defineStore('auth', () => {
	const DEFAULT_STATE = {
		token: getLocalStorageItem<ApiV1.AuthToken>('inflcr-token'),
		impersonationToken: getLocalStorageItem<ApiV1.AuthToken>('inflcr-impersonation-token'),
	};

	const state = reactive(cloneDeep(DEFAULT_STATE));

	const setToken = (token: ApiV1.AuthToken, rememberMe: boolean) => {
		state.token = token;
		setLocalStorageItem<ApiV1.AuthToken>(
			EStorageKey.TOKEN,
			token,
			new Date(token.expires_at),
			rememberMe ? 'localStorage' : 'sessionStorage',
		);
	};

	const setImpersonationToken = (token: ApiV1.AuthToken) => {
		state.impersonationToken = token;
		setLocalStorageItem<ApiV1.AuthToken>(
			EStorageKey.IMPERSONATION_TOKEN,
			token,
			new Date(token.expires_at),
			'sessionStorage',
		);
	};

	const getTokenBasic = async (authTokenRequest: ApiV1.BasicAuthRequest) => {
		const authService = useAuthService();
		const token = await authService.getTokenBasic(authTokenRequest);
		if (!token) throw new Error();
		setToken(token, authTokenRequest.remember_me);
	};

	const getTokenTeamworks = async (authTokenRequest?: ApiV1.TeamworksAuthRequest) => {
		if (!authTokenRequest) return;
		const authService = useAuthService();
		const token = await authService.getTokenTeamworks(authTokenRequest);
		if (!token) throw new Error();
		setToken(token, authTokenRequest.remember_me);
	};

	const getTokenAxle = async (authTokenRequest?: ApiV1.AxleAuthRequest) => {
		if (!authTokenRequest) return;
		const authService = useAuthService();
		const token = await authService.getTokenAxle(authTokenRequest);
		if (!token) throw new Error();
		setToken(token, true);
	};

	const getTokenImpersonation = async (authTokenRequest: ApiV1.ImpersonationAuthRequest) => {
		const authService = useAuthService();
		const token = await authService.getTokenImpersonation(authTokenRequest);
		if (!token) throw new Error();
		setImpersonationToken(token);
	};

	const clearClientToken = () => {
		state.token = null;
		removeLocalStorageItem('inflcr-token');
	};

	const removeImpersonationToken = async (tokenUuid: string) => {
		try {
			const authService = useAuthService();
			await authService.deleteToken(tokenUuid);
		} catch (error) {
			handleError(error);
		} finally {
			clearClientImpersonationToken();
		}
	};

	const clearClientImpersonationToken = () => {
		state.impersonationToken = null;
		removeLocalStorageItem('inflcr-impersonation-token');
	};

	const $reset = () => {
		Object.assign(state, cloneDeep(DEFAULT_STATE));
	};

	return {
		...toRefs(state),
		setToken,
		getTokenBasic,
		getTokenTeamworks,
		getTokenAxle,
		getTokenImpersonation,
		clearClientToken,
		removeImpersonationToken,
		$reset,
	};
});
